import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import * as fabric from 'fabric';
import { PageTemplate } from './PageTemplate';
import type { PageTemplate as PageTemplateType, PaperSize } from '@/types/notebook';
import { PAPER_PIXELS } from '@/types/notebook';

export type ToolType = 'pen' | 'highlighter' | 'text' | 'select';

export type NotebookCanvasRef = {
  getCanvasData: () => string;
  loadCanvasData: (data: string) => void;
  getThumbnail: () => string | null;
  undo: () => void;
  redo: () => void;
  clear: () => void;
  deleteSelected: () => boolean;
  hasSelection: () => boolean;
  addImage: (imageUrl: string, options?: { maxWidth?: number; maxHeight?: number }) => Promise<void>;
};

type NotebookCanvasProps = {
  template: PageTemplateType;
  paperSize: PaperSize;
  initialData?: string;
  readOnly?: boolean;
  tool?: ToolType;
  penColor?: string;
  penWidth?: number;
  onCanvasChange?: (data: string) => void;
};

const DEFAULT_PEN_COLOR = '#000000';
const DEFAULT_PEN_WIDTH = 2;
const HIGHLIGHTER_OPACITY = 0.3;
const HIGHLIGHTER_WIDTH = 20;

export const NotebookCanvas = forwardRef<NotebookCanvasRef, NotebookCanvasProps>(
  (
    {
      template,
      paperSize,
      initialData,
      readOnly = false,
      tool = 'pen',
      penColor = DEFAULT_PEN_COLOR,
      penWidth = DEFAULT_PEN_WIDTH,
      onCanvasChange,
    },
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fabricRef = useRef<fabric.Canvas | null>(null);
    const [isReady, setIsReady] = useState(false);
    const historyRef = useRef<string[]>([]);
    const historyIndexRef = useRef(-1);
    const isLoadingRef = useRef(false);
    const lastLoadedDataRef = useRef<string | undefined>(undefined);

    const dimensions = PAPER_PIXELS[paperSize];

    // Initialize Fabric canvas
    useEffect(() => {
      if (!canvasRef.current) return;

      const canvas = new fabric.Canvas(canvasRef.current, {
        width: dimensions.width,
        height: dimensions.height,
        backgroundColor: 'transparent', // Transparent so PageTemplate shows through
        isDrawingMode: !readOnly && tool !== 'select' && tool !== 'text',
        selection: !readOnly,
      });

      // Initialize free drawing brush (required in Fabric 7.x)
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = penColor;
      canvas.freeDrawingBrush.width = penWidth;

      // Style the Fabric.js wrapper container for proper positioning
      const wrapperEl = canvas.wrapperEl;
      if (wrapperEl) {
        wrapperEl.style.position = 'absolute';
        wrapperEl.style.top = '0';
        wrapperEl.style.left = '0';
        wrapperEl.style.zIndex = '1';
      }

      fabricRef.current = canvas;
      setIsReady(true);

      // Load initial data if provided
      if (initialData && initialData !== '{}') {
        try {
          isLoadingRef.current = true;
          canvas.loadFromJSON(JSON.parse(initialData)).then(() => {
            canvas.renderAll();
            isLoadingRef.current = false;
            // Add to history
            const json = JSON.stringify(canvas.toJSON());
            historyRef.current = [json];
            historyIndexRef.current = 0;
          });
        } catch (e) {
          console.error('Failed to load canvas data:', e);
          isLoadingRef.current = false;
        }
      } else {
        // Initialize history with empty canvas
        const json = JSON.stringify(canvas.toJSON());
        historyRef.current = [json];
        historyIndexRef.current = 0;
      }

      // Set up change listener
      const handleChange = () => {
        if (isLoadingRef.current) return;

        const json = JSON.stringify(canvas.toJSON());

        // Add to history (remove any redo states)
        historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
        historyRef.current.push(json);
        historyIndexRef.current = historyRef.current.length - 1;

        // Keep history manageable
        if (historyRef.current.length > 50) {
          historyRef.current.shift();
          historyIndexRef.current--;
        }

        // Update the ref so we don't reload this data when it comes back from parent
        lastLoadedDataRef.current = json;
        onCanvasChange?.(json);
      };

      canvas.on('object:added', handleChange);
      canvas.on('object:modified', handleChange);
      canvas.on('object:removed', handleChange);
      canvas.on('path:created', handleChange);

      // Track what data was initially loaded
      lastLoadedDataRef.current = initialData;

      return () => {
        canvas.dispose();
        fabricRef.current = null;
      };
    }, [dimensions.width, dimensions.height]);

    // Reload canvas data when initialData changes (e.g., page switch)
    useEffect(() => {
      const canvas = fabricRef.current;
      if (!canvas || !isReady) return;

      // Only reload if the data actually changed from external source
      // (not from our own onCanvasChange callback)
      if (initialData === lastLoadedDataRef.current) return;

      console.log('[NotebookCanvas] Loading new page data');
      lastLoadedDataRef.current = initialData;
      isLoadingRef.current = true;

      const loadData = async () => {
        try {
          const parsed = initialData && initialData !== '{}' ? JSON.parse(initialData) : {};
          await canvas.loadFromJSON(parsed);
          canvas.renderAll();

          // Reset history for new page
          const json = JSON.stringify(canvas.toJSON());
          historyRef.current = [json];
          historyIndexRef.current = 0;
        } catch (e) {
          console.error('Failed to load canvas data:', e);
        } finally {
          isLoadingRef.current = false;
        }
      };

      loadData();
    }, [initialData, isReady]);

    // Update drawing mode based on tool
    useEffect(() => {
      const canvas = fabricRef.current;
      if (!canvas || !isReady) return;

      canvas.isDrawingMode = !readOnly && (tool === 'pen' || tool === 'highlighter');
      canvas.selection = !readOnly && tool === 'select';

      // Update cursor based on tool
      const cursor = readOnly ? 'default' : tool === 'text' ? 'text' : tool === 'select' ? 'default' : 'crosshair';
      canvas.defaultCursor = cursor;
      canvas.hoverCursor = cursor;
      if (canvas.wrapperEl) {
        canvas.wrapperEl.style.cursor = cursor;
      }

      if (canvas.isDrawingMode && canvas.freeDrawingBrush) {
        switch (tool) {
          case 'pen':
            canvas.freeDrawingBrush.color = penColor;
            canvas.freeDrawingBrush.width = penWidth;
            if ('opacity' in canvas.freeDrawingBrush) {
              (canvas.freeDrawingBrush as fabric.BaseBrush & { opacity: number }).opacity = 1;
            }
            break;
          case 'highlighter':
            canvas.freeDrawingBrush.color = penColor;
            canvas.freeDrawingBrush.width = HIGHLIGHTER_WIDTH;
            if ('opacity' in canvas.freeDrawingBrush) {
              (canvas.freeDrawingBrush as fabric.BaseBrush & { opacity: number }).opacity = HIGHLIGHTER_OPACITY;
            }
            break;
        }
      }
    }, [tool, penColor, penWidth, readOnly, isReady]);

    // Handle text tool clicks
    useEffect(() => {
      const canvas = fabricRef.current;
      if (!canvas || !isReady || readOnly) return;

      const handleMouseDown = (e: fabric.TPointerEventInfo<fabric.TPointerEvent>) => {
        if (tool !== 'text') return;
        if (e.target) return; // Clicked on existing object

        // Fabric.js 7.x: use scenePoint directly from event (not getViewportPoint)
        const pointer = e.scenePoint;
        const text = new fabric.IText('Type here...', {
          left: pointer.x,
          top: pointer.y,
          fontSize: 16,
          fontFamily: 'Arial, sans-serif',
          fill: penColor,
          // Fabric.js 7.x: origin defaults to 'center', set explicitly for expected behavior
          originX: 'left',
          originY: 'top',
        });

        canvas.add(text);
        canvas.setActiveObject(text);
        text.enterEditing();
        text.selectAll();
      };

      canvas.on('mouse:down', handleMouseDown);

      return () => {
        canvas.off('mouse:down', handleMouseDown);
      };
    }, [tool, penColor, readOnly, isReady]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getCanvasData: () => {
        if (!fabricRef.current) return '{}';
        return JSON.stringify(fabricRef.current.toJSON());
      },

      loadCanvasData: (data: string) => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        isLoadingRef.current = true;
        try {
          const parsed = data ? JSON.parse(data) : {};
          canvas.loadFromJSON(parsed).then(() => {
            canvas.renderAll();
            isLoadingRef.current = false;
          });
        } catch (e) {
          console.error('Failed to load canvas data:', e);
          isLoadingRef.current = false;
        }
      },

      getThumbnail: () => {
        const canvas = fabricRef.current;
        if (!canvas) return null;

        // Generate a smaller thumbnail
        const scale = 0.15; // 15% of original size
        return canvas.toDataURL({
          format: 'png',
          quality: 0.7,
          multiplier: scale,
        });
      },

      undo: () => {
        const canvas = fabricRef.current;
        if (!canvas || historyIndexRef.current <= 0) return;

        historyIndexRef.current--;
        const json = historyRef.current[historyIndexRef.current];
        isLoadingRef.current = true;
        canvas.loadFromJSON(JSON.parse(json)).then(() => {
          canvas.renderAll();
          isLoadingRef.current = false;
          onCanvasChange?.(json);
        });
      },

      redo: () => {
        const canvas = fabricRef.current;
        if (!canvas || historyIndexRef.current >= historyRef.current.length - 1) return;

        historyIndexRef.current++;
        const json = historyRef.current[historyIndexRef.current];
        isLoadingRef.current = true;
        canvas.loadFromJSON(JSON.parse(json)).then(() => {
          canvas.renderAll();
          isLoadingRef.current = false;
          onCanvasChange?.(json);
        });
      },

      clear: () => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        canvas.clear();
        canvas.backgroundColor = 'transparent';
        canvas.renderAll();
      },

      deleteSelected: () => {
        const canvas = fabricRef.current;
        if (!canvas) return false;

        const activeObjects = canvas.getActiveObjects();
        if (activeObjects.length === 0) return false;

        // Remove all selected objects
        activeObjects.forEach((obj) => canvas.remove(obj));
        canvas.discardActiveObject();
        canvas.renderAll();
        return true;
      },

      hasSelection: () => {
        const canvas = fabricRef.current;
        if (!canvas) return false;
        return canvas.getActiveObjects().length > 0;
      },

      addImage: async (imageUrl: string, options?: { maxWidth?: number; maxHeight?: number }) => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        const maxWidth = options?.maxWidth ?? dimensions.width * 0.8;
        const maxHeight = options?.maxHeight ?? dimensions.height * 0.6;

        try {
          // Load image using Fabric.js 7.x API
          const img = await fabric.FabricImage.fromURL(imageUrl, {
            crossOrigin: 'anonymous',
          });

          // Scale to fit within max dimensions while maintaining aspect ratio
          const imgWidth = img.width ?? 100;
          const imgHeight = img.height ?? 100;
          const scaleX = maxWidth / imgWidth;
          const scaleY = maxHeight / imgHeight;
          const scale = Math.min(scaleX, scaleY, 1); // Don't scale up

          img.scale(scale);

          // Center on canvas
          img.set({
            left: (dimensions.width - imgWidth * scale) / 2,
            top: (dimensions.height - imgHeight * scale) / 2,
            originX: 'left',
            originY: 'top',
          });

          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
        } catch (e) {
          console.error('Failed to add image to canvas:', e);
        }
      },
    }), [onCanvasChange, dimensions.width, dimensions.height]);

    return (
      <Box
        sx={{
          position: 'relative',
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: '#ffffff',
          boxShadow: 2,
          overflow: 'hidden',
        }}
      >
        {/* Template background */}
        <PageTemplate template={template} paperSize={paperSize} />

        {/* Canvas - Fabric.js will wrap this and we style the wrapper in useEffect */}
        <canvas ref={canvasRef} />
      </Box>
    );
  }
);

NotebookCanvas.displayName = 'NotebookCanvas';
